import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { toast } from '@/components/ui/use-toast';
import { formatErrorMessage } from '@/lib/layout/Header/utils';
import { AutoFilled } from '@/lib/pages/forms/components/AutoFilled';
import { formatDate } from '@/lib/utils/dates';

import {
  glacierPolicyApiCreateMidtermTransactionMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';
import { TransactionType } from '@/lib/heyapi/types.gen';

import type { Policy } from '../../../heyapi/types.gen';

export function ReinstateForm({ policy }: { policy: Policy | undefined }) {
  const { id } = useParams();

  const queryClient = useQueryClient();
  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const createMidterm = useMutation({
    ...glacierPolicyApiCreateMidtermTransactionMutation(),
    onError: (error) => {
      toast({
        title: 'Error',
        description: formatErrorMessage(error),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: qk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Creating Reinstate Transaction</code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/pricing-preview`;
      }, 500);
    },
  });

  const handleSubmit = () => {
    createMidterm.mutate({
      body: {
        effective_date: newEffectiveDate,
        transaction_type: TransactionType.POLICY_REINSTATED,
      },
      path: {
        account_id: id!,
      },
    });
  };

  const policyEffectiveDate = policy?.details?.policyEffectiveDate;
  const latestTransactionEffectiveDate =
    policy?.latestTransaction.effectiveDate;

  // Set the minimum effective date for cancellation
  // policy?.details?.policyEffectiveDate OR policy?.latestTransaction.effectiveDate
  // which ever is the latest
  let cancelMinEffectiveDate = policyEffectiveDate;

  if (
    latestTransactionEffectiveDate &&
    policyEffectiveDate &&
    latestTransactionEffectiveDate > policyEffectiveDate
  ) {
    cancelMinEffectiveDate = latestTransactionEffectiveDate;
  }

  // Already a YYYY-MM-DD string; guard a missing value to avoid an invalid date.
  const newEffectiveDate = cancelMinEffectiveDate ?? '';

  const form = useForm({});

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-8"
      >
        <div>
          <AutoFilled
            label="Effective date"
            value={formatDate(newEffectiveDate)}
            stretch
          />
        </div>
        <Button type="submit">Confirm</Button>
      </form>
    </Form>
  );
}
